// ─── src/data/globalVendors.js ────────────────────────────────────────────────
// Global vendors shown on the homepage directory grid.
// Separate from data/vendors.js (Italy-specific) and data/italyVenues.js.
import { hydrateScores } from "../engine/index.js";
import { VENDOR_CATEGORIES } from "./geo.js";

// ── CATS, Browse dropdown categories ────────────────────────────────────────
// Derived from geo.js VENDOR_CATEGORIES (single source of truth).
// id === slug so CAT_ID_TO_GEO maps are no longer needed anywhere.

// Marketing labels for Browse UI (geo.js uses plain canonical labels)
const BROWSE_LABELS = {
  "wedding-planners": "Top Wedding Planners",
  "photographers":    "Best Photographers",
  "videographers":    "Best Videographers",
  "florists":         "Wedding Flowers",
  "event-design":     "Styling & Decor",
  "caterers":         "Wedding Caterers",
  "transport":        "Luxury Transport",
  "stationery":       "Stationery & Invitations",
  "accessories":      "Wedding Accessories",
};

// Text-symbol icons for the Browse dropdown UI (geo.js uses emoji for other contexts)
const BROWSE_ICONS = {
  "wedding-venues":   "⌂",
  "wedding-planners": "◎",
  "hair-makeup":      "✦",
  "photographers":    "◉",
  "videographers":    "▶",
  "bridal-wear":      "◇",
  "guest-attire":     "◆",
  "florists":         "✿",
  "event-design":     "❖",
  "wedding-cakes":    "◆",
  "accessories":      "◈",
  "stationery":       "✉",
  "health-beauty":    "✧",
  "celebrants":       "☆",
  "event-production": "▣",
  "caterers":         "◈",
  "entertainment":    "♪",
  "gift-registry":    "❑",
  "transport":        "➤",
  "jewellers":        "◇",
};

// Explicit Browse display order
const BROWSE_ORDER = [
  "wedding-venues", "wedding-planners", "hair-makeup", "photographers",
  "videographers", "bridal-wear", "guest-attire", "florists", "event-design",
  "wedding-cakes", "accessories", "stationery", "health-beauty", "celebrants",
  "event-production", "caterers", "entertainment", "gift-registry", "transport",
  "jewellers",
];

export const CATS = [
  { id: "all", label: "All Categories", icon: "◈" },
  ...BROWSE_ORDER
    .map((slug) => VENDOR_CATEGORIES.find((vc) => vc.slug === slug))
    .filter(Boolean)
    .map((vc) => ({
      id:    vc.slug,
      label: BROWSE_LABELS[vc.slug] || vc.label,
      icon:  BROWSE_ICONS[vc.slug]  || "◈",
    })),
];

export const COUNTRIES = [
  "Worldwide",
  "United Kingdom", "Italy", "France", "Greece", "Portugal", "Spain",
  "Cyprus", "USA", "Switzerland", "Hungary", "Malta", "Montenegro",
  "Turkey", "United Arab Emirates", "Asia", "Indian Ocean",
  "The Caribbean", "Africa",
  "America", "Australia", "Austria", "Canada", "Central America",
  "Costa Rica", "Croatia", "Denmark", "Germany", "Gibraltar",
  "Iceland", "Indonesia", "Ireland", "Jordan", "Malaysia", "Mexico",
  "New Zealand", "Norway", "Poland", "Qatar", "Romania", "Russia",
  "Slovenia", "South America", "Sweden", "The Czech Republic",
  "The Netherlands",
];

export const LOCATIONS = [
  { group: "Europe", items: [
    { country: "United Kingdom", cities: ["London", "Cotswolds", "Edinburgh", "Manchester", "Bath", "Lake District", "Cornwall", "Surrey", "Yorkshire", "Cheshire", "Kent", "Hampshire", "Devon", "Oxfordshire", "Berkshire"] },
    { country: "Italy", cities: ["Tuscany", "Amalfi Coast", "Lake Como", "Rome", "Venice", "Florence", "Puglia", "Sardinia", "Sicily", "Positano", "Ravello", "Sorrento", "Umbria", "Capri", "Milan"] },
    { country: "France", cities: ["Paris", "Provence", "French Riviera", "Bordeaux", "Loire Valley", "Normandy", "Champagne", "Dordogne", "Burgundy", "Corsica", "Lyon", "Chamonix"] },
    { country: "Greece", cities: ["Santorini", "Mykonos", "Athens", "Crete", "Corfu", "Rhodes", "Zakynthos", "Kefalonia", "Paros", "Naxos"] },
    { country: "Portugal", cities: ["Algarve", "Lisbon", "Porto", "Sintra", "Madeira", "Azores", "Douro Valley", "Cascais", "Comporta"] },
    { country: "Spain", cities: ["Marbella", "Barcelona", "Mallorca", "Ibiza", "Seville", "Madrid", "San Sebastián", "Tenerife", "Valencia", "Granada", "Menorca", "Formentera"] },
    { country: "Cyprus", cities: ["Paphos", "Limassol", "Ayia Napa", "Larnaca", "Protaras"] },
    { country: "Switzerland", cities: ["Lucerne", "Zermatt", "Interlaken", "Zurich", "Geneva", "Lake Geneva", "St. Moritz", "Lugano"] },
    { country: "Ireland", cities: ["Dublin", "Cork", "Galway", "Kerry", "Killarney", "Wicklow", "Clare", "Donegal"] },
    { country: "Croatia", cities: ["Dubrovnik", "Split", "Hvar", "Rovinj", "Zadar", "Istria"] },
    { country: "Austria", cities: ["Vienna", "Salzburg", "Innsbruck", "Tyrol", "Hallstatt"] },
    { country: "Germany", cities: ["Munich", "Berlin", "Hamburg", "Black Forest", "Heidelberg", "Dresden", "Lake Constance"] },
    { country: "Hungary", cities: ["Budapest", "Lake Balaton"] },
    { country: "Malta", cities: ["Valletta", "Mdina", "Gozo"] },
    { country: "Montenegro", cities: ["Kotor", "Budva", "Sveti Stefan"] },
    { country: "Denmark", cities: ["Copenhagen", "Aarhus"] },
    { country: "Iceland", cities: ["Reykjavik", "South Coast"] },
    { country: "Norway", cities: ["Oslo", "Bergen", "Tromsø", "Lofoten"] },
    { country: "Sweden", cities: ["Stockholm", "Gothenburg", "Malmö"] },
    { country: "The Netherlands", cities: ["Amsterdam", "The Hague", "Utrecht"] },
    { country: "The Czech Republic", cities: ["Prague", "Český Krumlov"] },
    { country: "Poland", cities: ["Krakow", "Warsaw", "Gdańsk"] },
    { country: "Romania", cities: ["Bucharest", "Transylvania", "Cluj-Napoca"] },
    { country: "Slovenia", cities: ["Ljubljana", "Lake Bled"] },
    { country: "Turkey", cities: ["Istanbul", "Antalya", "Cappadocia", "Bodrum", "Fethiye", "Kas"] },
    { country: "Gibraltar", cities: [] },
  ]},
  { group: "Americas", items: [
    { country: "USA", cities: ["New York", "California", "Hawaii", "Florida", "Napa Valley", "Charleston", "Aspen", "Miami", "Los Angeles", "San Francisco", "Nashville", "Savannah", "Lake Tahoe", "Martha's Vineyard", "Austin"] },
    { country: "Canada", cities: ["Toronto", "Vancouver", "Montreal", "Banff", "Niagara", "Whistler", "Ottawa", "Victoria"] },
    { country: "Mexico", cities: ["Cancún", "Tulum", "Los Cabos", "Puerto Vallarta", "Mexico City", "Riviera Maya", "San Miguel de Allende"] },
    { country: "Costa Rica", cities: ["Manuel Antonio", "Guanacaste", "Arenal"] },
    { country: "The Caribbean", cities: ["Barbados", "Jamaica", "St. Lucia", "Bahamas", "Turks & Caicos", "Antigua", "Aruba", "Dominican Republic", "Grenada", "Trinidad & Tobago", "U.S. Virgin Islands", "British Virgin Islands"] },
    { country: "Brazil", cities: ["Rio de Janeiro", "São Paulo", "Buzios", "Fernando de Noronha"] },
    { country: "Argentina", cities: ["Buenos Aires", "Mendoza", "Bariloche"] },
    { country: "Colombia", cities: ["Cartagena", "Bogotá", "Medellín"] },
    { country: "Peru", cities: ["Lima", "Cusco", "Sacred Valley"] },
    { country: "Chile", cities: ["Santiago", "Valparaíso", "Patagonia"] },
  ]},
  { group: "Asia", items: [
    { country: "Thailand", cities: ["Koh Samui", "Phuket", "Chiang Mai", "Bangkok", "Krabi"] },
    { country: "Indonesia", cities: ["Bali", "Lombok", "Jakarta", "Ubud", "Nusa Dua"] },
    { country: "Malaysia", cities: ["Langkawi", "Kuala Lumpur", "Penang", "Borneo"] },
    { country: "India", cities: ["Udaipur", "Jaipur", "Goa", "Kerala", "Mumbai", "Delhi", "Rajasthan"] },
    { country: "Sri Lanka", cities: ["Colombo", "Galle", "Kandy", "Bentota"] },
    { country: "Philippines", cities: ["Boracay", "Palawan", "Cebu", "Siargao"] },
    { country: "Japan", cities: ["Tokyo", "Kyoto", "Okinawa", "Hokkaido"] },
    { country: "Vietnam", cities: ["Hoi An", "Da Nang", "Phu Quoc", "Ho Chi Minh City"] },
    { country: "Singapore", cities: [] },
    { country: "Hong Kong", cities: [] },
    { country: "South Korea", cities: ["Seoul", "Jeju Island"] },
    { country: "China", cities: ["Shanghai", "Beijing", "Hainan"] },
  ]},
  { group: "Middle East & Indian Ocean", items: [
    { country: "United Arab Emirates", cities: ["Dubai", "Abu Dhabi", "Ras Al Khaimah"] },
    { country: "Qatar", cities: ["Doha"] },
    { country: "Jordan", cities: ["Amman", "Dead Sea", "Aqaba"] },
    { country: "Oman", cities: ["Muscat", "Salalah"] },
    { country: "Maldives", cities: ["Malé", "Ari Atoll", "Baa Atoll"] },
    { country: "Mauritius", cities: ["Grand Baie", "Flic en Flac", "Le Morne"] },
    { country: "Seychelles", cities: ["Mahé", "Praslin", "La Digue"] },
  ]},
  { group: "Africa", items: [
    { country: "South Africa", cities: ["Cape Town", "Johannesburg", "Winelands", "Kruger", "Garden Route", "Durban", "Franschhoek"] },
    { country: "Morocco", cities: ["Marrakech", "Fez", "Essaouira", "Tangier"] },
    { country: "Kenya", cities: ["Nairobi", "Masai Mara", "Diani Beach", "Lamu"] },
    { country: "Tanzania", cities: ["Zanzibar", "Serengeti", "Arusha"] },
    { country: "Egypt", cities: ["Cairo", "Luxor", "Hurghada", "Sharm El Sheikh"] },
    { country: "Nigeria", cities: ["Lagos", "Abuja"] },
    { country: "Ghana", cities: ["Accra", "Cape Coast"] },
  ]},
  { group: "Oceania", items: [
    { country: "Australia", cities: ["Sydney", "Melbourne", "Gold Coast", "Byron Bay", "Hunter Valley", "Yarra Valley", "Great Barrier Reef", "Perth", "Margaret River", "Tasmania"] },
    { country: "New Zealand", cities: ["Queenstown", "Auckland", "Waiheke Island", "Hawke's Bay", "Wanaka", "Rotorua"] },
    { country: "Fiji", cities: ["Denarau", "Mamanuca Islands", "Yasawa Islands"] },
    { country: "French Polynesia", cities: ["Bora Bora", "Tahiti", "Moorea"] },
  ]},
];

export const GLOBAL_VENDORS = []; // fake demo data removed, real listings only

hydrateScores(GLOBAL_VENDORS);
